import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  model,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AppService,
  CanvasService,
  DetectionService,
  MessageService,
  MlTrackingService,
  ScreenshotService,
  SimulationService,
  TaskService,
} from '@services';
import { SceneStore } from '@stores';
import '@tensorflow/tfjs-backend-webgl';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { debounceTime } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

const SECONDS_TO_SKIP = 5;

@Component({
  selector: 'bo-simulation-view',
  templateUrl: './simulation-view.component.html',
  standalone: true,
  imports: [AngularSvgIconModule, CommonModule, FormsModule, TranslateModule],
})
export class SimulationViewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  video = viewChild<ElementRef>('video');
  recording = viewChild<ElementRef>('recording');
  screen = viewChild<ElementRef>('screen');
  overlayCanvas = viewChild<ElementRef>('overlayCanvas');
  dynamicCanvas = viewChild<ElementRef>('dynamicCanvas');
  debugCanvas = viewChild<ElementRef>('debugCanvas');
  screenshotCanvas = viewChild<ElementRef>('screenshotCanvas');
  canvasElement = viewChild<ElementRef>('canvasElement');

  isScreenshot = model(false);

  protected appService = inject(AppService);
  protected canvasService = inject(CanvasService);
  private detectionService = inject(DetectionService);
  private destroyRef = inject(DestroyRef);
  private messageService = inject(MessageService);
  private mlTrackingService = inject(MlTrackingService);
  private router = inject(Router);
  protected sceneStore = inject(SceneStore);
  protected taskService = inject(TaskService);
  public translateService = inject(TranslateService);
  public simulationService = inject(SimulationService);
  private screenshotService = inject(ScreenshotService);

  videoStream: MediaStream;
  screenshotTimeOut: NodeJS.Timeout;
  isStarted = false;
  isRunning?: boolean;
  cancelText: string;
  randomNum?: string;
  intervalId: number;

  constructor() {
    this.killStreams();
    toObservable(this.appService.demoMode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.swapVideoElement();
      });

    effect(() => {
      if (this.appService.cameraId()) {
        this.swapVideoElement();
      }
    });

    effect(() => {
      if (this.simulationService.state() === 'simulation') {
        const interval = setInterval(() => {
          if (this.videoStream) {
            this.simulationService.startRecording(this.videoStream);
            clearInterval(interval);
          }
        }, 200);
      }
    });

    this.translateService
      .get('recordingPage.cancelText')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text: string) => {
        this.cancelText = text;
      });

    toObservable(this.canvasService.screen)
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.updateOverlays();
      });

    effect(
      () => {
        if (
          this.simulationService.recordingTime() === this.sceneStore.maxTime()
        ) {
          this.messageService.showDetectionMessage({
            text: 'You reached maximum exercise duration',
            identifier: 'maxTimeReached',
          });
        }
      },
      { allowSignalWrites: true },
    );
  }

  takeScreenshot(): void {
    if (!this.isScreenshot()) {
      clearTimeout(this.screenshotTimeOut);
      this.isScreenshot.set(true);

      this.screenshotTimeOut = setTimeout(() => {
        this.isScreenshot.set(false);
      }, 2000);

      const blob = this.screenshotService.takeScreenshot(
        this.appService.demoMode() ? this.video() : this.recording(),
      );

      const screenshotCanvas: HTMLCanvasElement =
        this.screenshotCanvas().nativeElement;
      const screenshotContext = screenshotCanvas.getContext('2d');
      if (screenshotContext) {
        const image = new Image();
        image.src = URL.createObjectURL(blob);
        image.onload = () => {
          screenshotContext.drawImage(
            image,
            0,
            0,
            screenshotCanvas.width,
            screenshotCanvas.height,
          );
        };
      }

      this.sceneStore.addScreenshot(blob);
    }
  }

  swapVideoElement() {
    const interval = setInterval(() => {
      if (this.overlayCanvas()) {
        if (!this.appService.demoMode()) {
          navigator.mediaDevices
            .getUserMedia({
              audio: false,
              video: this.appService.cameraConfig(),
            })
            .then((stream) => {
              this.videoStream = stream;
              this.recording().nativeElement.srcObject = stream;
              this.recording().nativeElement.play();

              const camRatio = [
                stream.getVideoTracks()[0].getSettings().width,
                stream.getVideoTracks()[0].getSettings().height,
              ];

              const width = Math.max(camRatio[0], camRatio[1]);
              const height = Math.min(camRatio[0], camRatio[1]);

              this.canvasService.camera.set({ width, height });

              this.updateOverlays();
            })
            .catch();
          this.detectionService.detectionSources.set(
            'insideView',
            this.recording(),
          );
          clearInterval(interval);
        } else {
          this.killStreams();
          this.detectionService.detectionSources.set(
            'insideView',
            this.video(),
          );
          this.canvasService.video.set({
            width: this.video().nativeElement.videoWidth,
            height: this.video().nativeElement.videoHeight,
          });
          if (this.canvasService.video()) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = this.canvasService.video().width;
            canvas.height = this.canvasService.video().height;

            const drawVideoFrame = () => {
              ctx.drawImage(
                this.video().nativeElement,
                0,
                0,
                canvas.width,
                canvas.height,
              );
              requestAnimationFrame(drawVideoFrame);
            };

            this.video().nativeElement.addEventListener('play', () => {
              drawVideoFrame();
            });

            this.videoStream = canvas.captureStream();
          }

          this.updateOverlays();
          clearInterval(interval);
        }
      }
    }, 200);
  }

  updateOverlays() {
    const shift = this.canvasService.shift();
    this.overlayCanvas()!.nativeElement.width =
      this.canvasService.overlay().width;
    this.overlayCanvas()!.nativeElement.height =
      this.canvasService.overlay().height;

    this.overlayCanvas()!.nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';

    this.dynamicCanvas().nativeElement.width =
      this.canvasService.overlay().width;
    this.dynamicCanvas().nativeElement.height =
      this.canvasService.overlay().height;
    this.dynamicCanvas().nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';

    this.debugCanvas().nativeElement.width = this.canvasService.overlay().width;
    this.debugCanvas().nativeElement.height =
      this.canvasService.overlay().height;
    this.debugCanvas().nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';
  }

  fastForward() {
    this.video().nativeElement.currentTime =
      this.video().nativeElement.currentTime + SECONDS_TO_SKIP;
  }

  fastBackward() {
    this.video().nativeElement.currentTime =
      this.video().nativeElement.currentTime - SECONDS_TO_SKIP;
  }

  pauseVideo() {
    if (this.video().nativeElement.paused) {
      this.video().nativeElement.play();
    } else {
      this.video().nativeElement.pause();
    }
  }

  replayVideo() {
    if (this.video().nativeElement.paused) this.video().nativeElement.play();
    else this.video().nativeElement.load();
  }

  killStreams() {
    this.videoStream?.getTracks().forEach((track) => {
      if (track) {
        track.stop();
        this.videoStream.removeTrack(track);
      }
    });
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.pauseCamera();
    } else {
      this.resumeCamera();
    }
  }

  pauseCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((track) => (track.enabled = false));
    }
  }

  resumeCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((track) => (track.enabled = true));
    }
  }

  onFinish(): void {
    this.simulationService.stopRecording();
    this.simulationService.stopRecordingTimer();
    this.appService.fullScreen.set(false);
    this.isRunning = false;
    this.mlTrackingService.prepareTrackingFile();
    if (
      !this.taskService.taskListCompleted() &&
      !this.simulationService.recordingTime()
    ) {
      this.appService.appState.set('canceled');
      this.router.navigate(['/end'], {
        queryParamsHandling: 'preserve',
      });
    } else {
      this.simulationService.state.set('screenshot');
    }
  }

  ngOnInit(): void {
    this.randomNum = uuidv4();

    document.onkeydown = (event) => {
      switch (event.key) {
        case 'ArrowLeft': {
          this.fastBackward();
          break;
        }
        case 'ArrowRight': {
          this.fastForward();
          break;
        }
        case 'Space': {
          this.pauseVideo();
          break;
        }
      }
    };

    this.appService.fastBackward
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fastBackward();
      });

    this.appService.fastForward
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fastForward();
      });

    this.appService.pauseVideo
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pauseVideo();
      });

    this.appService.replayVideo
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.replayVideo();
      });

    this.simulationService.isSimulationRunning.set(true);
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this),
    );
    window.addEventListener('beforeunload', (event) => {
      event.returnValue = true;
    });
  }

  ngAfterViewInit() {
    this.simulationService.startDetection();

    this.appService.takeScreenshot
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.takeScreenshot();
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.intervalId);
    this.simulationService.isSimulationRunning.set(false);
    this.killStreams();
  }
}
